import React, { useState, useMemo } from 'react';
import { useSession } from '../context/SessionContext';
import { SessionManager, t } from '../services/sessionManager';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';

interface CleanOptions {
  extraSpaces: boolean;
  extraLineBreaks: boolean;
  removeLineBreaks: boolean;
  trim: boolean;
  normalizeQuotes: boolean;
  removeTabs: boolean;
  removeEmojis: boolean;
  removeHidden: boolean;
}

interface Preset {
  id: string;
  label: string;
  options: Partial<CleanOptions>;
}

const Dashboard: React.FC = () => {
  const { language, isAuthenticated, login, apiData, refreshSession } = useSession();
  const [inputText, setInputText] = useState('');
  
  // All options selected by default as requested
  const [options, setOptions] = useState<CleanOptions>({
    extraSpaces: true,
    extraLineBreaks: true,
    removeLineBreaks: true,
    trim: true,
    normalizeQuotes: true,
    removeTabs: true,
    removeEmojis: true,
    removeHidden: true,
  });
  
  const [copied, setCopied] = useState(false);
  const [referralCopied, setReferralCopied] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const presets: Preset[] = [
    {
      id: 'socialMedia',
      label: 'socialMedia',
      options: { extraSpaces: true, normalizeQuotes: true, removeTabs: true, extraLineBreaks: true, removeLineBreaks: false, removeHidden: true }
    },
    {
      id: 'plainText',
      label: 'plainText',
      options: { extraSpaces: true, extraLineBreaks: true, trim: true, normalizeQuotes: true, removeLineBreaks: false, removeHidden: true }
    },
    {
      id: 'webSafe',
      label: 'webSafe',
      options: { normalizeQuotes: true, removeTabs: true, extraSpaces: true, removeLineBreaks: false, removeHidden: true }
    },
    {
      id: 'singleLine',
      label: 'singleLine',
      options: { removeLineBreaks: true, extraSpaces: true, trim: true, extraLineBreaks: false, removeHidden: true }
    }
  ];

  const toggleOption = (key: keyof CleanOptions) => {
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const applyPreset = async (preset: Preset) => {
    setErrorMessage(null);
    
    const Joisst = (window as any).Joisst;
    if (!Joisst) {
      setErrorMessage("Joisst Platform SDK is not loaded yet.");
      return;
    }

    const currentUserId = Joisst.user.userId;

    if (Joisst.user.hearts < 1) {
      if (Joisst.user.isAnonymous) {
        Joisst.redirectToRegister();
      } else {
        setErrorMessage(t('insufficientHearts', language));
      }
      return;
    }

    try {
      await Joisst.spendHearts(1);
      setOptions(prev => ({ ...prev, ...preset.options }));
      await refreshSession();
    } catch (err: any) {
      if (Joisst.user.isAnonymous) {
        Joisst.redirectToRegister();
      } else {
        setErrorMessage(err.message || t('insufficientHearts', language));
      }
    }
  };

  const cleanedText = useMemo(() => {
    let text = inputText;
    
    // 1. Process character-level replacements
    if (options.removeHidden) {
      // Remove Zero-Width, Invisible Characters, Em Space (U+2003) and Wide Space (U+3000)
      text = text.replace(/[\u200B\u200C\u200D\uFEFF\u2003\u3000\u2002\u2060\u2063]/g, '');
    } else {
      // Basic fallback for absolutely invisible formatting that breaks standard text viewing
      text = text.replace(/[\uFEFF]/g, '');
    }

    if (options.removeTabs) {
      text = text.replace(/\t/g, ' ');
    }
    
    if (options.normalizeQuotes) {
      text = text.replace(/[\u201C\u201D]/g, '"').replace(/[\u2018\u2019]/g, "'");
    }
    
    if (options.removeEmojis) {
      const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E6}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{1F018}-\u{1F093}\u{1F191}-\u{1F19A}\u{1F3FB}-\u{1F3FF}]/gu;
      text = text.replace(emojiRegex, '');
    }

    // 2. Handle Line Breaks
    if (options.removeLineBreaks) {
      text = text.replace(/\r?\n|\r/g, ' ');
    } else if (options.extraLineBreaks) {
      text = text.replace(/\n{3,}/g, '\n\n');
    }

    // 3. Handle Extra Horizontal Spaces
    if (options.extraSpaces) {
      text = text.replace(/[ \t\u00A0]{2,}/g, ' ');
      if (!options.removeLineBreaks) {
        text = text.replace(/^[ \t\u00A0]+/gm, '');
      }
    }

    // 4. Final Trimming (Global and Per-Paragraph)
    if (options.trim) {
      if (options.removeLineBreaks) {
        // If line breaks are gone, simple trim is enough
        text = text.trim();
      } else {
        // Multi-line trim: removes spaces from start/end of every line AND whole block
        text = text.split(/\r?\n/).map(line => line.trim()).join('\n').trim();
      }
    }

    return text;
  }, [inputText, options]);

  const stats = useMemo(() => {
    const chars = cleanedText.length;
    const words = cleanedText.trim() ? cleanedText.trim().split(/\s+/).length : 0;
    return { chars, words };
  }, [cleanedText]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(cleanedText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy', err);
    }
  };

  const handleCopyReferral = async () => {
    if (!apiData?.referralCode) return;
    const referralLink = `${window.location.origin}/?ref=${apiData.referralCode}`;
    try {
      await navigator.clipboard.writeText(referralLink);
      setReferralCopied(true);
      setTimeout(() => setReferralCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy referral link', err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <p className="text-gray-500 dark:text-slate-400 text-sm">
            {t('tagline', language)}
          </p>
          <p className="text-gray-900 dark:text-white text-lg font-semibold mt-1">
            {t('instruction', language)}
          </p>
        </div>

        <div className="flex gap-2">
          {!isAuthenticated && (
            <Button variant="accent" onClick={login} className="text-xs">
              {t('signIn', language)}
            </Button>
          )}
          <Button variant="secondary" onClick={() => setInputText('')} className="text-xs">
            {t('clearAll', language)}
          </Button>
          <Button variant="accent" onClick={handleCopy} className="text-xs min-w-[100px]">
            {copied ? t('copied', language) : t('copyText', language)}
          </Button>
        </div>
      </div>

      {errorMessage && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm animate-pulse">
          {errorMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1 space-y-6">
          {/* Options Card */}
          <Card className="space-y-4" padding="p-4">
            <h3 className="text-xs font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-2">{t('options', language)}</h3>
            <div className="flex flex-col gap-2">
              {[
                { id: 'extraSpaces', label: 'removeExtraSpaces' },
                { id: 'extraLineBreaks', label: 'removeExtraLineBreaks' },
                { id: 'removeLineBreaks', label: 'removeLineBreaks' },
                { id: 'trim', label: 'trimWhitespace' },
                { id: 'normalizeQuotes', label: 'normalizeQuotes' },
                { id: 'removeTabs', label: 'removeTabs' },
                { id: 'removeEmojis', label: 'removeEmojis' },
                { id: 'removeHidden', label: 'removeHidden' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => toggleOption(opt.id as keyof CleanOptions)}
                  className={`flex items-center justify-between px-3 py-2.5 rounded-xl text-left text-sm font-medium transition-all border ${
                    options[opt.id as keyof CleanOptions]
                      ? 'bg-teal-50 border-teal-200 text-teal-700 dark:bg-teal-900/20 dark:border-teal-900/30 dark:text-teal-400'
                      : 'bg-white border-gray-100 text-gray-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400'
                  }`}
                >
                  <span className="leading-tight pr-2">{t(opt.label as any, language)}</span>
                  <div className={`shrink-0 w-4 h-4 rounded-full flex items-center justify-center ${options[opt.id as keyof CleanOptions] ? 'bg-accent dark:bg-accent-dark text-white dark:text-slate-900' : 'bg-gray-200 dark:bg-slate-700 text-transparent'}`}>
                    <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
            <div className="pt-4 mt-4 border-t border-gray-100 dark:border-slate-800">
               <div className="flex justify-between items-center text-xs text-gray-500 dark:text-slate-400">
                  <span>{t('totalChars', language)}:</span>
                  <span className="font-mono text-gray-900 dark:text-white font-bold">{stats.chars}</span>
               </div>
               <div className="flex justify-between items-center text-xs text-gray-500 dark:text-slate-400 mt-2">
                  <span>{t('totalWords', language)}:</span>
                  <span className="font-mono text-gray-900 dark:text-white font-bold">{stats.words}</span>
               </div>
            </div>
          </Card>

          {/* Presets Card */}
          <Card className="space-y-4" padding="p-4">
            <div className="space-y-1">
              <h3 className="text-xs font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">
                {t('presets', language)} <span className="lowercase font-normal opacity-70">({t('optional', language)})</span>
              </h3>
              <p className="text-[10px] text-gray-400 dark:text-slate-500 leading-tight">
                {t('presetHelper', language)}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => applyPreset(preset)}
                  className="flex items-center justify-between px-3 py-2.5 rounded-xl text-left text-sm font-bold transition-all border border-transparent bg-[#F3F4F6] text-[#111827] hover:bg-gray-200 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700 shadow-sm"
                >
                  <span>{t(preset.label as any, language)}</span>
                  <div className="flex items-center text-[10px] bg-red-500 text-white px-1.5 py-0.5 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-2.5 h-2.5 mr-0.5">
                      <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
                    </svg>
                    1
                  </div>
                </button>
              ))}
            </div>
          </Card>

          {/* Share & Earn Card */}
          {isAuthenticated && apiData?.referralCode && (
            <Card className="space-y-4" padding="p-4">
              <div className="space-y-1">
                <h3 className="text-xs font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">
                  {t('shareAndEarn', language)}
                </h3>
                <p className="text-[10px] text-gray-400 dark:text-slate-500 leading-tight">
                  {t('referralText', language)}
                </p>
              </div>
              <Button
                variant="primary"
                onClick={handleCopyReferral}
                className="w-full text-xs font-bold"
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                }
              >
                {referralCopied ? t('copied', language) : t('copyLink', language)}
              </Button>
            </Card>
          )}
        </div>

        <div className="lg:col-span-3 space-y-4">
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder={t('pastePlaceholder', language)}
            className="w-full h-96 p-4 rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-accent outline-none transition-all resize-none font-mono text-sm shadow-sm"
          />
          <div className="relative group">
            <textarea
              readOnly
              value={cleanedText}
              className="w-full h-96 p-4 rounded-2xl border border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-950 text-gray-600 dark:text-slate-300 transition-all resize-none font-mono text-sm shadow-inner"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;